/**
 * What the offline side activities keep in the save (`SaveData.activities`, save v5; `mayhem` since v6; the
 * spray bay's matt / rim / neon / ride on the motor's build since v8):
 * tiers unlocked, best scores, the matchbooks found, the build sheet of the player's motor at Halloran's
 * and the mayhem challenges' best scores and stamps. Plain JSON; `sanitizeActivities` turns anything (an
 * old save, a hand-edited one) into a valid record.
 */
import { emptyMayhem, sanitizeMayhem, type MayhemRecord } from './mayhem';

/** A tiered, replayable activity: the street races and the minicab shifts. */
export interface TieredRecord {
  /** Highest tier unlocked (1-based; 0 = not played / not open yet — the unlock flag decides). */
  unlocked: number;
  /** The tier the giver offers next (1-based). */
  next: number;
  /** Best score per tier (index 0 = tier 1): race = seconds (0 = none), cabs = pounds taken (0 = none). */
  best: number[];
  /** Runs finished (any result) and runs won / paid. */
  runs: number;
  wins: number;
  /** Tiers won (races) or paid out in full (shifts that hit the target) at least once. */
  cleared: number[];
}

/** Halloran's build sheet: what Des has done to the player's motor. */
export interface CarBuild {
  /** Paint (hex), or null = the car's own colour. */
  paint: number | null;
  /** Metallic flake finish. */
  metallic: boolean;
  /** Wheel style id (src/vehicles/meshes.ts `WheelStyle`), or null = the model's own. */
  wheels: string | null;
  /** Stripes / vinyl roof / contrast roof / trade band colour (whichever the body takes), or null. */
  trim: number | null;
  /** Personal plate text, or null = the car's own. */
  plate: string | null;
  /** Performance tune stage 0..2 (offline only; docs/ACTIVITIES-OFFLINE.md). */
  tune: number;
  /** Pike's spray bay (save v8, src/garage): flat matt paint (wins over metallic). */
  matt: boolean;
  /** Rim colour (hex), or null = the wheel's own finish. */
  rim: number | null;
  /** Underglow colour (hex), or null = none. */
  neon: number | null;
  /** Ride height -1 lowered, 0 stock, +1 raised (visual only). */
  ride: number;
  /** Camber step 0..3 and track step -1..2 (shared/garage.ts; visual only). */
  camber: number;
  track: number;
}

export interface SavedMotor {
  /** Vehicle spec id of the car Des last worked on. */
  spec: string;
  build: CarBuild;
}

export interface ActivitiesSave {
  race: TieredRecord;
  cab: TieredRecord;
  /** Matchbook ids found (src/activities/matchbooks.ts). */
  matchbooks: string[];
  /** Matchbook milestones already paid (counts: 4, 8, 12, 16). */
  matchbookMilestones: number[];
  /** The player's motor (null until Des has worked on something). */
  motor: SavedMotor | null;
  /** Pounds spent at Halloran's workshop (stat). */
  garageSpent: number;
  /** Mayhem challenges (the half-bricks): best score and stamp per challenge, runs (src/activities/mayhem.ts). */
  mayhem: MayhemRecord;
}

/** Four of each since Kiln Common (WP-X7): the Kiln Loop and the Saturday shift. */
export const RACE_TIERS = 4;
export const CAB_TIERS = 4;

export function emptyTiered(tiers: number): TieredRecord {
  return { unlocked: 1, next: 1, best: new Array<number>(tiers).fill(0), runs: 0, wins: 0, cleared: [] };
}

export function emptyBuild(): CarBuild {
  return { paint: null, metallic: false, wheels: null, trim: null, plate: null, tune: 0, matt: false, rim: null, neon: null, ride: 0, camber: 0, track: 0 };
}

export function emptyActivities(): ActivitiesSave {
  return { race: emptyTiered(RACE_TIERS), cab: emptyTiered(CAB_TIERS), matchbooks: [], matchbookMilestones: [], motor: null, garageSpent: 0, mayhem: emptyMayhem() };
}

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown, fallback: number, lo: number, hi: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;
const hex = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 0xffffff ? v : null);
const PLATE_RE = /^[A-Z0-9 ]{1,8}$/;
const WHEEL_RE = /^[a-z]{2,12}$/;

function sanitizeTiered(raw: unknown, tiers: number): TieredRecord {
  const d = emptyTiered(tiers);
  if (!isObj(raw)) return d;
  let unlocked = Math.round(num(raw.unlocked, 1, 1, tiers));
  const best = Array.isArray(raw.best) ? raw.best : [];
  const cleared = Array.isArray(raw.cleared) ? [...new Set(raw.cleared.filter((x): x is number => Number.isInteger(x) && (x as number) >= 1 && (x as number) <= tiers))].sort() : [];
  // A save from before a tier was added: the old top tier already cleared opens the new one (the same rule
  // as `advanceTier` — clearing the highest tier unlocked opens the next).
  if (cleared.includes(unlocked) && unlocked < tiers) unlocked++;
  return {
    unlocked,
    next: Math.min(unlocked, Math.round(num(raw.next, unlocked, 1, tiers))),
    best: d.best.map((_, i) => Math.round(num(best[i], 0, 0, 99_999) * 10) / 10),
    runs: Math.round(num(raw.runs, 0, 0, 1e7)),
    wins: Math.round(num(raw.wins, 0, 0, 1e7)),
    cleared,
  };
}

export function sanitizeBuild(raw: unknown): CarBuild {
  const d = emptyBuild();
  if (!isObj(raw)) return d;
  const plate = typeof raw.plate === 'string' && PLATE_RE.test(raw.plate) ? raw.plate : null;
  const wheels = typeof raw.wheels === 'string' && WHEEL_RE.test(raw.wheels) ? raw.wheels : null;
  return {
    paint: hex(raw.paint),
    metallic: raw.metallic === true,
    wheels,
    trim: hex(raw.trim),
    plate,
    tune: Math.round(num(raw.tune, 0, 0, 2)),
    matt: raw.matt === true,
    rim: hex(raw.rim),
    neon: hex(raw.neon),
    ride: Math.round(num(raw.ride, 0, -1, 1)),
    camber: Math.round(num(raw.camber, 0, 0, 3)),
    track: Math.round(num(raw.track, 0, -1, 2)),
  };
}

/** Clamp and default every field. Never throws. */
export function sanitizeActivities(raw: unknown): ActivitiesSave {
  const d = emptyActivities();
  if (!isObj(raw)) return d;
  const ids = (v: unknown): string[] =>
    Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && /^[a-z0-9-]{1,40}$/.test(x)))].slice(0, 64) : [];
  const counts = Array.isArray(raw.matchbookMilestones)
    ? [...new Set(raw.matchbookMilestones.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x > 0 && x <= 64))]
    : [];
  let motor: SavedMotor | null = null;
  if (isObj(raw.motor) && typeof raw.motor.spec === 'string' && /^[a-z]{2,16}$/.test(raw.motor.spec)) {
    motor = { spec: raw.motor.spec, build: sanitizeBuild(raw.motor.build) };
  }
  return {
    race: sanitizeTiered(raw.race, RACE_TIERS),
    cab: sanitizeTiered(raw.cab, CAB_TIERS),
    matchbooks: ids(raw.matchbooks),
    matchbookMilestones: counts.sort((a, b) => a - b),
    motor,
    garageSpent: Math.round(num(raw.garageSpent, 0, 0, 99_999_999)),
    mayhem: sanitizeMayhem(raw.mayhem),
  };
}
